import { Field, Form, Formik, FormikProps } from 'formik';
import Cookies from 'js-cookie';
import moment from 'moment';
import React, { ReactElement, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import * as Yup from 'yup';
import { getCurrentUser } from '../../api/actions/get-current-user';
import { login } from '../../api/actions/login-action';
import { requestOneTimeCode } from '../../api/actions/user/user-request-one-time-code';
import { Button } from '../../components/button/button';
import { LinkButton } from '../../components/link-button/link-button';
import { Loader } from '../../components/loader/loader';
import { AppConfig } from '../../config/app-config';
import { CurrentUserContext } from '../../contexts/current-user-context';
import { IDictionary } from '../../types/dictionary';
import styles from './login-page.module.scss';
import { toast } from 'react-toastify';

interface IForm {
  email: string;
  password: string;
  code?: string;
}

interface IProps {
  email?: string;
  onSuccessLogin?(configurationFinished: boolean): void;
  signupLinkCallback?: any;
  introMessage?: string;
  handleToggle?: () => void;
}

export enum LoginUIState {
  INITIAL_LOGIN = 'initial-login',
  LOGIN_OPTIONS = 'login-options',
  PASSWORD_LOGIN = 'password-login',
  CODE_LOGIN = 'code-login',
}
export function LoginComponent(props: IProps) {
  Cookies.remove(AppConfig.tokenName);

  const currentUserContext = React.useContext(CurrentUserContext);
  currentUserContext?.setState(undefined);

  const [pips] = useState(
    new URL(window.location.href).searchParams.get('pipssource') || undefined
  );
  const [loading, setLoading] = useState(false);
  const [loginUIState, setLoginUIState] = useState<string | undefined>(LoginUIState.INITIAL_LOGIN);

  const history = useHistory();

  const signup = () => {
    if (!props.signupLinkCallback) {
      history.replace('/signup');
    } else {
      props.signupLinkCallback();
    }
  };

  const handleSubmit = async (
    values: IForm,
    {
      setSubmitting,
      setErrors,
    }: {
      setSubmitting: (status: boolean) => void;
      setErrors: (errors: IDictionary<string>) => void;
    }
  ) => {
    setLoading(true);
    try {
      let pwd = values.password;
      let oneTimeCode = false;
      if (loginUIState === LoginUIState.CODE_LOGIN) {
        pwd = values.code || '';
        oneTimeCode = true;
      }
      const { accessToken, configurationFinished } = await login(
        values.email,
        pwd,
        oneTimeCode
      );

      Cookies.set(AppConfig.tokenName, accessToken, {
        domain: new URL(location.href).hostname,
        expires: moment().add(100, 'days').toDate(),
      });



      // currentUserContext?.setState(await getCurrentUser());

      props.onSuccessLogin?.(configurationFinished);
    } catch (err) {
      setSubmitting(false);
      setErrors({
        password:
          'The password or email you entered is incorrect. Please try again.',
      });
      setLoading(false);
      toast.error(`Error ${err}`);
    }
  };

  const getValidationSchema = () => {
    return Yup.object({
      email: Yup.string()
        .required('Please enter your email address')
        .email('Please enter valid email address'),
      password: Yup.string().when([], {
        is: () => loginUIState === LoginUIState.PASSWORD_LOGIN,
        then: (schema) => schema.required('Please enter your password'),
        otherwise: (schema) => schema.notRequired(),
      }),
      code: Yup.string().when([], {
        is: () => loginUIState === LoginUIState.CODE_LOGIN,
        then: (schema) => schema.required('Please enter your one time code'),
        otherwise: (schema) => schema.notRequired(),
      }),
 
    });
  };

  const handleOneTimeCodeRequest = async (email: string) => {
    try {
      setLoginUIState(LoginUIState.CODE_LOGIN)
      await requestOneTimeCode(email);
    } catch (err) {
      toast.error(`Error ${err}`);
    }
  };

  const renderLoginForm = ({
    errors,
    touched,
    submitForm,
    values,
  }: FormikProps<IForm>): ReactElement => {
    return (
      <div className={styles.container}>
        <div className={styles.subcontainer}>
          <h1>
            ThinkHumanTV
            <br />
            {!props.introMessage ? (
              <>
                <span>
                  <br />
                  Boost emotional skills and wellness <br />
                  while watching Netflix, Disney+ and Peacock.
                </span>
              </>
            ) : (
              <>
                <span>
                  <br />
                  {props.introMessage}
                </span>
              </>
            )}
          </h1>
          <Form noValidate>
            {pips && (
              <div>
                Log in or sign up with your organization&apos;s (e.g. college)
                email to earn PIPs Rewards.
              </div>
            )}
            <div className="form-item"></div>
            <div className="form-item">
              <label>
                <div className="form-label required">Email</div>
                <Field type="email" name="email" disabled={!!props.email} />
              </label>
              <div className="errors">{touched.email && errors.email}</div>
            </div>
            {loginUIState === LoginUIState.PASSWORD_LOGIN && (
            <>
            <div className="form-item">
              <label>
                <div className="form-label required">Password</div>
                <Field type="password" name="password" />
              </label>
              <div className="errors">
                {touched.password && errors.password}
              </div>
              <Link to="/forgot-password">Forgot password</Link>
            </div>

            </>)}
            {loginUIState === LoginUIState.CODE_LOGIN && (
              <>
              <div className="form-item">
                <label>
                  <div className="form-label required">Code</div>
                  <Field type="text" name="code" />
                </label>
                <div className="errors">
                {touched.code && errors.code}
              </div>                
              </div>
              </>
            )}
            {loginUIState === LoginUIState.INITIAL_LOGIN && (
              <div className="form-buttons single">
                <Button
                  onClick={() => {
                    setLoginUIState(LoginUIState.LOGIN_OPTIONS);
                  }}
                >
                  Continue to Log In
                </Button>
              </div>
            )}
            {(loginUIState === LoginUIState.PASSWORD_LOGIN || loginUIState === LoginUIState.CODE_LOGIN) && (
            <>
            <div className="form-buttons single">
              {loading ? (
                <Loader />
              ) : (
                <Button onClick={submitForm}>
                  <span>Login</span>
                </Button>
              )}
            </div>
            <div className="form-buttons single">
                <LinkButton
                            onClick={() => {
                              setLoginUIState(LoginUIState.LOGIN_OPTIONS);
                            }}
                          >
                            Try a different login method
                  </LinkButton>
              </div>
            </>)}

          {loginUIState === LoginUIState.LOGIN_OPTIONS && (
            <div className="form-buttons single" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <LinkButton
                onClick={() => {
                  setLoginUIState(LoginUIState.PASSWORD_LOGIN);
                }}
              >
                Log in with password
              </LinkButton>
              <LinkButton
                onClick={() => {
                  handleOneTimeCodeRequest(values.email);
                }}
              >
                Log in with one time code
              </LinkButton>
            </div>
       
            )}

            <hr />
       
            <div className="form-buttons single">
              <LinkButton
                onClick={() => {
                  if (props.handleToggle) {
                    props.handleToggle();
                  } else {
                    signup();
                  }
                }}
              >
                Sign up
              </LinkButton>
            </div>
          </Form>
        </div>
      </div>
    );
  };

  return (
    <>
      <Formik
        initialValues={{
          email: props.email
            ? props.email
            : AppConfig.isDebug
            ? AppConfig.testUserEmail
            : '',
          password: '',
          code: '',
        }}
        validationSchema={getValidationSchema}
        onSubmit={handleSubmit}
      >
        {renderLoginForm}
      </Formik>
    </>
  );
}
